/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import some from 'lodash/some';
import get from 'lodash/get';
import filter from 'lodash/filter';
import flatten from 'lodash/flatten';
import includes from 'lodash/includes';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { gnoment } from 'shared-ui/gnoment';
import { ISSUANCE_STATUS, SEASON_TYPES } from './constants';

// Requires that all keys and values be unique.
const transformIntoBidirectionalMap = (_map) => {
    const ret = new Map();
    _map.forEach((k, v) => {
        ret.set(k, v);
        ret.set(v, k);
    });
    return ret;
};

const issuanceStatuses = transformIntoBidirectionalMap(ISSUANCE_STATUS);

export function isSeasonOpen(seasons = []) {
    if (!seasons.length) {
        return false;
    }
    // if present day is in any or some seasons
    const today = gnoment().local();
    return some(
        seasons.map((season) => {
            const start = gnoment(season.display_start).local();
            //Pushes end date to end of the day (UTC) then changes to local time
            const end = gnoment(season.end_date).endOf('day').local();
            return today.isBetween(start, end) && season.type !== SEASON_TYPES.Lottery;
        })
    );
}

// Gets props.currentDate if it exists, otherwise gets the current time.
// Useful for faking what time it is for debugging purposes
export const getCurrentTime = (props) => {
    if (props.currentDate) {
        return gnoment.isGnoment(props.currentDate)
            ? props.currentDate.utc()
            : gnoment(props.currentDate).utc();
    }

    let currentDate = gnoment().utc();

    // enable time machine date query param only in dev
    if (process.env.NODE_ENV === 'development') {
        const searchParams = new URLSearchParams(window.location.search);
        const maybeDateString = searchParams.get('date');

        if (maybeDateString) {
            currentDate = gnoment(maybeDateString).utc();
        }
    }

    return currentDate;
};

export const isInvalidLotteryAward = (props) => {
    const { permit, lottery, issuance } = props;
    const incorrectStatus =
        issuance.status !== issuanceStatuses.get('Awarded') ||
        props.issuance.status === issuanceStatuses.get('Accepted');
    if (incorrectStatus) {
        return true;
    }
    const currentTime = getCurrentTime(props);
    const announcedTimestamp = get(lottery, 'lottery.announced_at', false);
    if (
        announcedTimestamp &&
        gnoment(announcedTimestamp).tz(permit.time_zone).isAfter(currentTime)
    ) {
        return true;
    }
    return false;
};

export const getEntrances = (permit, divisionId, divisionSpecificEntrances = false) => {
    const divLength = permit.divisions ? Object.keys(permit.divisions).length : 0;
    let entrances = [];
    if (divisionSpecificEntrances && divLength > 1) {
        const reservationDivision = permit?.divisions?.[divisionId];
        const reservationDivisionEntries = reservationDivision?.entry_ids ?? [];
        entrances = flatten(
            map(reservationDivisionEntries, (entrance_id) => {
                return filter(permit?.entrances ?? [], {
                    is_entry: true,
                    id: entrance_id,
                });
            })
        );
    } else {
        entrances = sortBy(filter(permit?.entrances ?? [], { is_entry: true }), 'id');
    }

    return entrances;
};

export const getExits = (permit, divisionId, divisionSpecificExits = false) => {
    const divLength = permit.divisions ? Object.keys(permit.divisions).length : 0;
    let exits = [];
    if (divisionSpecificExits && divLength > 1) {
        const reservationDivision = permit?.divisions?.[divisionId];
        const reservationDivisionExits = reservationDivision?.exit_ids ?? [];
        exits = flatten(
            map(reservationDivisionExits, (exit_id) => {
                return filter(permit?.entrances ?? [], { is_exit: true, id: exit_id });
            })
        );
    } else {
        exits = sortBy(filter(permit?.entrances ?? [], { is_exit: true }), 'id');
    }

    return exits;
};

export const getEntrancesV2 = (permit, divisionId) => {
    const entrances = sortBy(
        filter(permit?.entrances ?? [], (e) => {
            return (
                e?.entrance_mapping?.is_entry &&
                (e?.entrance_mapping?.is_entry_all ||
                    includes(e?.entrance_mapping?.entry_for, divisionId))
            );
        }),
        'id'
    );
    return entrances;
};

export const getExitsV2 = (permit, divisionId) => {
    const exits = sortBy(
        filter(permit?.entrances ?? [], (e) => {
            return (
                e?.entrance_mapping?.is_exit &&
                (e?.entrance_mapping?.is_exit_all ||
                    includes(e?.entrance_mapping?.exit_for, divisionId))
            );
        }),
        'id'
    );
    return exits;
};

export const getIssueStationsV2 = (permit, divisionId) => {
    const issueStations = sortBy(
        filter(permit?.entrances ?? [], (e) => {
            return (
                e?.entrance_mapping?.is_issue_station &&
                (e?.entrance_mapping?.is_issue_station_all ||
                    includes(e?.entrance_mapping?.issue_station_for, divisionId))
            );
        }),
        'viewOrder'
    );
    return issueStations;
};

export const matchPermitTypeFromMapping = (permitId, desiredPermitType, permitMapping) =>
    permitMapping?.hasOwnProperty(desiredPermitType) &&
    permitMapping[desiredPermitType]?.includes(permitId);

export const SAME_DAY_DEAD_LINE_MSG = (amPmTime) =>
    `You can only book a reservation on the same day up until ${amPmTime}. It is now past that time at this location.`;
